import React, { useState, useEffect } from 'react';
import { IonCard, IonCardContent, IonImg, IonText, IonAlert, IonSelect, IonSelectOption, IonButton, IonIcon } from '@ionic/react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { useTreeStore } from './stateManagement/treeZustand';
import { shuffleList } from '../foo/quiz';
import { arrowBack, caretDown, checkmarkCircleOutline, gameController, helpCircle, home, library } from 'ionicons/icons';
import { _defaultButtonColor, _dict } from './constants';
import { useGlobalStore } from './stateManagement/globalZustand';
import { displayPic } from './usePhotoGallery';
import CommonHeader from './CommonHeader';
import { useHistory } from 'react-router';
import { useURLStore } from './stateManagement/URLZustand';
import { label2LabelToShow } from '../foo/cards';

interface AutoQuizReorderChunksProps {
    hammingDistance: number;
    onComplete: any;
    chunks: string[];
}

interface AutoQuizFillGapsProps {
    onComplete: any;
    chunks: string[];
    gaps: number;
}

interface AutoQuizMultipleChoiceProps {
    currentChunkIndex: number;
    onComplete: any;
    chunks: string[];
    invertGame: boolean; // New prop to determine the game mode
}


const CARD_WIDTH = '120px';
const CARD_HEIGHT = '150px';
const FONT_SIZE = '24px';
const GREEN = '#2ecc73';
const RED = '#ff9999';
const DEFAULT_CARD_STYLE = {
    minHeight: CARD_HEIGHT, display: 'flex',
    alignItems: 'center', justifyContent:"center",
    flexGrow: 1, fontSize: FONT_SIZE  };



const reorder = (list: string[], startIndex: number, endIndex: number): string[] => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
};

const playBeep = () => {
    const audioCtx = new ((window as any).AudioContext || (window as any).webkitAudioContext)();
    const playNote = (frequency: number, startTime: number, duration: number, volume = 0.2, type = 'sine') => {
        const oscillator = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();
        
        oscillator.frequency.value = frequency;
        oscillator.type = type;
        oscillator.connect(gainNode);
        
        gainNode.connect(audioCtx.destination);
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.01); // Quick fade-in
        gainNode.gain.setValueAtTime(volume, startTime + duration - 0.1);
        gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + duration); // Fade out smoothly
        
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
    };

    const chord = [
        { freq: 1046.5/4, type: 'sine' }, // C6
        { freq: 1318.51/4, type: 'sine' }, // E6
        { freq: 1567.98/3, type: 'sine' }  // G6
    ];

    const startTime = audioCtx.currentTime;
    const noteDuration = 0.3;
    const finalChordDuration = 0.3; 
    const finalChordStartTime = startTime + (chord.length - 1) * 0.1 + noteDuration - 0.2;

    // Play initial quick succession notes leading to the final chord
    chord.slice(0, -1).forEach((note, index) => {
        playNote(note.freq, startTime + index * 0.1, noteDuration, 0.2, note.type);
    });

    // Play the final chord with a longer duration for a satisfying finish
    chord.forEach(note => {
        playNote(note.freq, finalChordStartTime, finalChordDuration, 0.4, note.type);
    });
};


const AutoQuizReorderChunks: React.FC<AutoQuizReorderChunksProps> = ({
    chunks,
    onComplete,
    hammingDistance,
  }) => {
    const { l2Image } = useTreeStore();
    const { lang } = useGlobalStore();
    const [orderedChunks, setOrderedChunks] = useState<string[]>([]);
    const [correctOrder, setCorrectOrder] = useState<string[]>([]);
    const [isCorrect, setIsCorrect] = useState<boolean>(false);
    const [isDraggable, setIsDraggable] = useState<boolean>(true);
  
    useEffect(() => {
      setOrderedChunks(shuffleList(chunks, hammingDistance));
      setCorrectOrder([...chunks]);
    }, [chunks, hammingDistance]);
  
    useEffect(() => {
      if (isCorrect) {
        playBeep();
      }
    }, [isCorrect]);
  
    const onDragEnd = (result: DropResult) => {
      if (!result.destination) {
        return;
      }
  
      const items = reorder(
        orderedChunks,
        result.source.index,
        result.destination.index
      );
  
      setOrderedChunks(items);
  
      if (JSON.stringify(items) === JSON.stringify(correctOrder)) {
        setIsCorrect(true);
        setIsDraggable(false);
      }
    };
  
    return (
      <>
        <h2 style={{ textAlign: 'center' }}>{_dict.reorderTheCards[lang]}</h2>
        <p style={{ textAlign: 'center' }}>{_dict.followTheSameOrderOfTheCardsAsTheyAppearFromLeftToRight[lang]}</p>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable" direction="horizontal">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  style={{ display: 'flex', flexWrap: 'wrap', gap: '0px', alignItems: 'stretch' }}
                >
                  {orderedChunks.map((chunk, index) => (
                    <Draggable key={`${chunk}-${index}`} draggableId={`${chunk}-${index}`} index={index} isDragDisabled={!isDraggable}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={{
                            ...provided.draggableProps.style,
                            backgroundColor: isCorrect ? GREEN : 'transparent',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-end',
                          }}
                        >
                          <IonCard style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: CARD_HEIGHT }}>
                            <IonCardContent style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', flexGrow: 1 }}>
                              {displayPic(l2Image[chunk], false)}
                              <IonText style={{ fontSize: FONT_SIZE }}>{label2LabelToShow(chunk, lang)}</IonText>
                            </IonCardContent>
                          </IonCard>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
        {isCorrect && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
            <IonButton
              color="warning"
              onClick={onComplete}
              style={{ fontSize: '1.2rem', padding: '0.8rem 2rem' }}
              size="large"
            >
              {_dict.continue[lang]}
              <IonIcon icon={caretDown} />
            </IonButton>
          </div>
        )}
      </>
    );
  };
  








const AutoQuizFillGaps: React.FC<AutoQuizFillGapsProps> = ({ chunks, gaps, onComplete }) => {
    const { l2Image } = useTreeStore();
    const {lang} = useGlobalStore();

    const [sentence, setSentence] = useState<string[]>(() => {
        // Step 1: Copy the original array
        let tempChunks = [...chunks];
        
        // Step 2: Create a Set to track which positions have been chosen
        let chosenIndexes = new Set();
        
        // Keep inserting empty strings until we've reached the desired number of gaps
        while(chosenIndexes.size < gaps) {
          let randomIndex = Math.floor(Math.random() * tempChunks.length);
      
          // Only insert an empty string if this position hasn't been chosen yet
          if (!chosenIndexes.has(randomIndex)) {
            tempChunks[randomIndex] = '';
            chosenIndexes.add(randomIndex);
          }
        }
      
        return tempChunks;
      });
      
    const [selectedGapIndex, setSelectedGapIndex] = useState<number | null>(null);
    const [shuffledOptions, setShuffledOptions] = useState<string[]>(() => {
        // Identify the chunks that have been replaced by gaps in the sentence
        const replacedChunks = chunks.filter((chunk, index) => sentence[index] === '');
    
        // Shuffle these replaced chunks to create the options
        return shuffleList(replacedChunks, replacedChunks.length);
    });
    const [answerFeedback, setAnswerFeedback] = useState<Record<number, boolean | null>>({});
    const [isCorrect, setIsCorrect] = useState<boolean>(false);

    useEffect(() => {
        // Map through the sentence array to check if each word matches the original chunk,
        // ignoring the positions that are empty strings in the sentence array.
        const isCorrect = sentence.every((word, index) => {
            return chunks[index] === word;
        });
    
        setIsCorrect(isCorrect);
    }, [sentence, chunks]);

    useEffect(() => {
        if(isCorrect){
            playBeep();
        }
    }, [isCorrect]);
    


    const selectGap = (index: number) => {

        // Check if the current index is a gap by seeing if the sentence at that index is an empty string.
        if (sentence[index] !== '' && sentence[index] === chunks[index]) {
            return; // If it's not an empty string, it's not a gap, so exit the function.
        }
        // Toggle selection for the gap: if the same gap is selected again, deselect it.
        if (index === selectedGapIndex) {
            setSelectedGapIndex(null);
        } else {
            setSelectedGapIndex(index);
        }
    };
    

    const chooseAnswer = (word: string) => {
        if (selectedGapIndex !== null) {
            let newSentence = [...sentence];
            newSentence[selectedGapIndex] = word;
            setSentence(newSentence);

            // Update feedback
            let newFeedback = { ...answerFeedback };
            newFeedback[selectedGapIndex] = chunks[selectedGapIndex] === word;
            setAnswerFeedback(newFeedback);

            // Reset selected gap
            setSelectedGapIndex(null);
        }
    };

    const getBackgroundColor = (index: number) => {
        if (answerFeedback[index] === true) return GREEN; // Lighter green color
        if (answerFeedback[index] === false) return RED;
        return 'white'; // Default color for all cards
    };

    const renderCardContent = (word: string) => (
        <>
            {word ? displayPic(l2Image[word], false) : <IonIcon icon={helpCircle} size="large" style={{ fontSize: FONT_SIZE }} />}
                    <IonText style={{ textAlign: 'center' , color:"black", fontSize: FONT_SIZE}}>{label2LabelToShow(word, lang)}</IonText>
        </>
    );




    return (
        <>
        <h2 style={{ textAlign: 'center' }}>{_dict.guessMissingCards[lang]}</h2>
        <p style={{ textAlign: 'center' }}>{_dict.clickOnTheMissingCardAndSelectTheRightOneFromPossibleAnswers[lang]}</p>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
            <div style={{ display: 'flex', gap: '0px' }}>
                {sentence.map((word, index) => (
                    <IonCard
                        key={index}
                        onClick={() => {selectGap(index)}}
                        style={{
                            backgroundColor: getBackgroundColor(index),
                            minHeight: CARD_HEIGHT,
                            minWidth: CARD_WIDTH,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: selectedGapIndex === index ? '2px red solid' : '',
                            cursor: index < gaps ? 'pointer' : 'default', // Only allow selecting gaps
                        }}
                    >
                        <IonCardContent style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', flexGrow: 1 }}>
                            {renderCardContent(word)}
                        </IonCardContent>
                    </IonCard>
                ))}
            </div>
            
            {selectedGapIndex !== null && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
                <div style={{ display: 'flex', gap: '0px' }}>
                    {shuffledOptions.map((word, index) => (
                        <IonCard
                            key={index}
                            onClick={() => chooseAnswer(word)}
                            color="light"
                            style={{
                                ...DEFAULT_CARD_STYLE,
                                minHeight: CARD_HEIGHT,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                
                            }}
                        >
                            <IonCardContent style={{ minHeight: CARD_HEIGHT, textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', flexGrow: 1 }}>
                                {renderCardContent(word)}
                            </IonCardContent>
                        </IonCard>
                    ))}
                </div>
            </div>
)}



        </div>
        {isCorrect && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                <IonButton
                    color="warning"
                    onClick={() => { onComplete(); }}
                    style={{ fontSize: '1.2rem', padding: '0.8rem 2rem' }}
                    size="large"
                >
                    {_dict.continue[lang]}
                    <IonIcon icon={caretDown} />
                </IonButton>
            </div>
        )}
        </>
    );
};


const AutoQuizMultipleChoice: React.FC<AutoQuizMultipleChoiceProps> = ({
    chunks, invertGame, onComplete,
    currentChunkIndex
}) => {
    const { l2Image } = useTreeStore();
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [isSelectionDisabled, setIsSelectionDisabled] = useState<boolean>(false);
    const [isCorrect, setIsCorrect] = useState<boolean>(false);
    const [shuffledList, setShuffledList] = useState<string[]>(() => shuffleList(chunks, chunks.length));
    const {lang} = useGlobalStore();

    
    const currentChunk = chunks[currentChunkIndex];
    const correctImage = l2Image[currentChunk];
    const checkIfImageIsValid = (image: string | undefined) => {
        return image !== undefined && image !== '' && image !== null && image.length > 10;
    }

  
    const handleOptionSelect = (chunk: string) => {
      if (isSelectionDisabled) return;
  
      setSelectedOption(chunk);
      let newIsCorrect = invertGame ? l2Image[chunk] === correctImage : chunk === currentChunk
      setIsCorrect(newIsCorrect);
      if (newIsCorrect) {
        setIsSelectionDisabled(true); // Disable further selection after correct answer
      }
    };

    useEffect(() => {
        if(isCorrect){
          playBeep();
        }
          
    },[isCorrect]);

    
    const renderGameContent = () => {
        
        if (invertGame) {
            // Game mode: Guess the text from the given image
            
            if (!checkIfImageIsValid(correctImage)) {
                onComplete();
                return <></>;
            }
            return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' ,
                }}>
                    <IonCard style={DEFAULT_CARD_STYLE} >
                        <IonCardContent style={{ textAlign: 'center' }}>
                            <IonImg src={l2Image[currentChunk]} style={{
                                width: CARD_WIDTH, height: CARD_HEIGHT, margin: '0 auto',
                                fontSize: FONT_SIZE }} />
                        </IonCardContent>
                    </IonCard>
                    <p>{_dict.whatTextCorrespondToTheGivenImage[lang]}</p>
                    <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '10px' }}>
                        {shuffledList.map((chunk, index) => {
                            const isSelected = chunk === selectedOption;
                            const cardStyle = {
                                ...DEFAULT_CARD_STYLE,
                                backgroundColor: isSelected ? (chunk === currentChunk ? GREEN : RED) : 'transparent'
                            };

                            return (
                                <IonCard key={index} onClick={() => handleOptionSelect(chunk)} style={cardStyle}>
                                    <IonCardContent style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                        <IonText style={{ fontSize: FONT_SIZE }}>{label2LabelToShow(chunk, lang)}</IonText>
                                    </IonCardContent>
                                </IonCard>
                            );
                        })}
                    </div>
                </div>
            );
        } else {
            if (!checkIfImageIsValid(correctImage)) {
                onComplete();
                return <></>;
            }
            // Original game mode: Guess the image from the given text
            return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
                    <IonCard style={DEFAULT_CARD_STYLE}>
                        <IonCardContent style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <IonIcon icon={helpCircle} size='large' style={{ fontSize: FONT_SIZE, display: 'block', margin: '0 auto' }} />
                            <IonText style={{ fontSize: FONT_SIZE, marginTop: '10px' }}>{label2LabelToShow(currentChunk, lang)}</IonText>
                        </IonCardContent>
                    </IonCard>
                    <p>{_dict.whatImageCorrespondToTheGivenText[lang]}</p>
                    <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '10px' }}>
                        {shuffledList.map((chunk, index) => {
                            if (!l2Image[chunk]) {
                                return null; // Skip rendering if no image is associated with the chunk
                            }

                            const isSelected = chunk === selectedOption;
                            const cardStyle = {
                                backgroundColor: isSelected ? (chunk === currentChunk ? GREEN : RED) : 'transparent',
                            };

                            return (
                                <IonCard key={index} onClick={() => handleOptionSelect(chunk)} style={cardStyle}>
                                    <IonCardContent style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                        <IonImg src={l2Image[chunk] ?? ''} />
                                    </IonCardContent>
                                </IonCard>
                            );
                        })}
                    </div>
                </div>
            );
    }
    };

    return (
    <>
      <div>
        {renderGameContent()}
      </div>
      {isCorrect && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
            <IonButton
                color="warning"
                onClick={() => { onComplete() }}
                style={{ fontSize: '1.2rem', padding: '0.8rem 2rem' }}
                size="large"
            >
                {_dict.continue[lang]}
                <IonIcon icon={caretDown} />
            </IonButton>
        </div>
    )}
    </>
    );
};

const AutoQuiz: React.FC = () => {
    const history = useHistory();
    const { URLVars, getURLTo } = useURLStore();
    const { nodes } = useTreeStore();
    const { lang } = useGlobalStore();

    

    
    // Min Requirement : at least chunks size = 5 !
    const { l2Image } = useTreeStore();
    const [globalIndex, setGlobalIndex] = useState<number>(0);


    let chunks = nodes[URLVars.nodePath!]
    if(chunks === undefined){
        return <></>;
    }
    chunks = chunks.filter((chunk:string) => label2LabelToShow(chunk, lang) !== "");

    const onComplete = () => {
        setGlobalIndex(globalIndex+1)
    }

    
    let sequence: any[] = [];
    
    sequence = [...sequence, ...chunks.map((chunk, index) => {
        if(l2Image[chunk] === undefined){
            return null;
        }else{
            // Generate the sequence of components based on sentences and their types
            return <AutoQuizMultipleChoice
                    key={`multiplechoice-${index}-invert`}
                    chunks={chunks}
                    currentChunkIndex={index}
                    invertGame={true}
                    onComplete={onComplete}
            />;
        }
    }).filter((component) => component !== null)];

    sequence = [...sequence, ...chunks.map((chunk, index) => {
        if(l2Image[chunk] === undefined){
            return null;
        }else{
            // Generate the sequence of components based on sentences and their types
            return <AutoQuizMultipleChoice
                    key={`multiplechoice-${index}`}
                    chunks={chunks}
                    currentChunkIndex={index}
                    invertGame={false}
                    onComplete={onComplete}
            />;
        }
    }).filter((component) => component !== null)];

    sequence = [...sequence, ...[<AutoQuizReorderChunks
        key={`reorder-2`}
        onComplete={onComplete}
        hammingDistance={2}
        chunks={chunks}
    />,
    <AutoQuizReorderChunks
        key={`reorder-4`}
        onComplete={onComplete}
        hammingDistance={chunks.length}
        chunks={chunks}
    />]];

    sequence = [...sequence, ...[<AutoQuizFillGaps
        key={`fillgaps-2`}
        onComplete={onComplete}
        gaps={2}
        chunks={chunks} />,
        <AutoQuizFillGaps
        key={`fillgaps-3`}
        onComplete={onComplete}
        gaps={3}
        chunks={chunks} />
    ]];
    

    let extraJSX=<>
    <IonButton shape="round" fill="solid" color="light" size="large" onClick={()=>{history.push(getURLTo().talkFromCustomNodePath(URLVars.nodePath!||""))}} >
        <IonIcon slot="icon-only" color="dark" icon={arrowBack} size="large"></IonIcon>
    </IonButton>
    <IonButton fill="solid" color="light" shape="round" onClick={() => {history.push(getURLTo().home())}}>
        <IonIcon slot="icon-only" color="dark" icon={home} size="large"></IonIcon>
    </IonButton>
    

    </>;




    return <>
        <CommonHeader extraJSX={extraJSX} />
        {globalIndex >= sequence.length ? 
            <>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <IonIcon
                        icon={checkmarkCircleOutline}
                        style={{ fontSize: '72px', color: '#10dc60' }} // Using Ionic's color variable for success
                    />
                </div>
                <IonText style={{ textAlign:"center" }} >
                <h1>{_dict.congrats[lang]}</h1>
                <p>{_dict.youCompletedTheQuiz[lang]}</p>
                </IonText>
                <IonButton  size="large" expand="block" style={{ width: '50%', margin:"auto", marginTop:"30px", "--background":_defaultButtonColor }} onClick={() => { window.location.reload(); }}>
                    {_dict.retakeTheQuiz[lang]}
                </IonButton>
            </>
        : sequence[globalIndex]}
    </>;
};


export default AutoQuiz;

